import PT from 'prop-types';
import React from 'react';

import { NAV_HEIGHT } from '~/common/constants/page';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Tabs from '~/ui/components/Tabs/Tabs';
import { useLockBodyScroll } from '~/ui/hooks/interaction';
import styles from '~/ui/styles';

import { tabPropType } from '../Tabs/Tab';
import { PX, PY } from './constants';

function PageLayout({
  bg = 'background',
  children,
  footer,
  header,
  isContentCentered = true,
  selectedTab,
  tabs,
  sidebar,
  onSelectTab,
  isContentFill = false,
  isPadded = true,
}) {
  const isPageHeaderVisible = useFlag(FlagType.ShowHeaderForFullscreenModals);
  useLockBodyScroll();

  const py = isPadded ? PY : 0;
  const px = isPadded ? PX : 0;
  const contentStyle = isContentCentered
    ? {
        height: '100%',
        maxWidth: isContentFill ? undefined : 'page-content-max-width',
        mx: 'auto',
      }
    : {};

  const Content = (
    <Box
      px={px}
      py={py}
      space={4}
      sx={{
        flex: '1 1 auto',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <Box sx={contentStyle}>
        {children}
        <Box pb={py} /> {/* spacer */}
      </Box>
    </Box>
  );
  return (
    <Layout
      direction="column"
      bg={bg}
      position="fixed"
      styles={{
        ...styles.layout.fullPage,
        zIndex: 'panel',
        top: isPageHeaderVisible ? `${NAV_HEIGHT}px` : 0,
      }}
      role="dialog"
    >
      {header}
      <FlexLayout sx={styles.layout.stretch}>
        <FlexLayout
          flex="1 1 auto"
          sx={{
            height: () => {
              let offset = 0;
              if (footer) {
                offset += NAV_HEIGHT;
              }
              if (header) {
                offset += NAV_HEIGHT;
              }
              if (isPageHeaderVisible) {
                offset += NAV_HEIGHT;
              }

              return offset !== 0 ? `calc(100vh - ${offset}px)` : '100vh';
            },
          }}
        >
          {sidebar}
          <Box
            sx={{
              flex: '1 1 auto',
              overflow: 'auto',
            }}
          >
            {tabs ? (
              <FlexLayout
                flexDirection="column"
                sx={{ height: '100%', overflow: 'hidden' }}
              >
                <Box bg="white">
                  <Tabs
                    selectedTab={selectedTab}
                    tabs={tabs}
                    onSelectTab={onSelectTab}
                  />
                </Box>
                {Content}
              </FlexLayout>
            ) : (
              Content
            )}
          </Box>
        </FlexLayout>
      </FlexLayout>
      {footer}
    </Layout>
  );
}

PageLayout.propTypes = {
  /** Page background color */
  bg: PT.string,
  /** main contents of the page layout */
  children: PT.node,
  /** Footer of page layout. */
  footer: PT.node,
  /** Use one of the supported headers of page layout. */
  header: PT.node,
  /** Is content container centered */
  isContentCentered: PT.bool,
  /** Selected tab key */
  selectedTab: PT.string,
  /** Use one of the supported sidebars of page layout */
  sidebar: PT.node,
  /** List of tab objects */
  tabs: PT.arrayOf(tabPropType.isRequired),
  /** Callback to set selected tab */
  onSelectTab: PT.func,
};

import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import SidebarSection from './SidebarSection';
import SidebarSectionItem from './SidebarSectionItem';
import SidebarTab from './SidebarTab';
import SidebarTabs from './SidebarTabs';

PageLayout.Footer = Footer;
PageLayout.Header = Header;
PageLayout.Sidebar = Sidebar;
PageLayout.SidebarSection = SidebarSection;
PageLayout.SidebarSectionItem = SidebarSectionItem;
PageLayout.SidebarTab = SidebarTab;
PageLayout.SidebarTabs = SidebarTabs;

export default PageLayout;
